import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  ClinicRetentionSummary,
  MetricsSummary,
  ProfesionalesMetrics,
  PvpMetrics,
  Sede,
} from '@/application/metrics';
import type { RetentionStatus } from '@/application/metrics/ownerPvpRetentionTypes';
import {
  listedRetentionStatus,
  presentRetentionStatus,
} from '@/application/metrics/pvpRetentionPresentation';
import { formatInteger } from '@/presentation/components/metrics/metricFormatters';
import { OwnerStatusBadge } from '@/presentation/components/metrics/OwnerStatusBadge';
import { formatSigned } from '@/presentation/utils/metricDeltas';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';

export interface BranchMainMetrics {
  sede: Sede;
  summary: MetricsSummary | null;
  pvp: PvpMetrics | null;
  professionals: ProfesionalesMetrics | null;
}

type UiStatus = 'critical' | 'attention' | 'healthy';
type QuickFilter = 'all' | 'attention' | 'declining' | 'healthy';

interface ClinicHubProfessionalsPanelProps {
  tenantId: string;
  period: string;
  clinicProfessionals: ProfesionalesMetrics | null;
  pvp: PvpMetrics | null;
  retention: ClinicRetentionSummary | null;
  branchMainMetrics: BranchMainMetrics[];
  isBranchMetricsLoading?: boolean;
  /** Sede del hub (URL); sincroniza el filtro local de sucursal. */
  hubSedeId?: number | null;
}

function toUiStatus(status: RetentionStatus | null): UiStatus | 'unknown' {
  if (!status) return 'unknown';
  if (status === 'critical') return 'critical';
  if (status === 'alert' || status === 'observation') return 'attention';
  return 'healthy';
}

function uiStatusRank(status: UiStatus | 'unknown', declining: boolean): number {
  if (status === 'critical') return 0;
  if (status === 'attention') return 1;
  if (declining) return 2;
  if (status === 'unknown') return 4;
  return 3;
}

function retentionStatusRank(status: RetentionStatus | null): number {
  if (status === 'critical') return 0;
  if (status === 'alert') return 1;
  if (status === 'observation') return 2;
  if (status === 'insufficient_history') return 3;
  if (status === null) return 5;
  return 4;
}

function trendSymbol(trend: string | undefined, pvpDelta: number | null): 'up' | 'down' | 'flat' {
  if (trend === 'up') return 'up';
  if (trend === 'down') return 'down';
  if (pvpDelta !== null) {
    if (pvpDelta < -0.3) return 'down';
    if (pvpDelta > 0.3) return 'up';
  }
  return 'flat';
}

/**
 * Tab Profesionales del hub: KPIs + tabla ejecutiva + comparativa por sucursal.
 * Sin cards ni desgloses anidados.
 */
export function ClinicHubProfessionalsPanel({
  tenantId,
  period,
  clinicProfessionals,
  pvp,
  retention,
  branchMainMetrics,
  isBranchMetricsLoading,
  hubSedeId = null,
}: ClinicHubProfessionalsPanelProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const navigate = useNavigate();

  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');
  const [branchFilter, setBranchFilter] = useState<number | 'all'>(() => hubSedeId ?? 'all');
  const [search, setSearch] = useState('');
  const [performanceOpen, setPerformanceOpen] = useState(true);
  const [branchCompareOpen, setBranchCompareOpen] = useState(false);

  useEffect(() => {
    setBranchFilter(hubSedeId ?? 'all');
  }, [hubSedeId]);

  const retentionByProfessional = useMemo(() => {
    const map = new Map<
      number,
      {
        status: RetentionStatus;
        pvpActual: number;
        variacionAbs: number | null;
        variacionPct: number | null;
      }
    >();
    const ingest = (
      alert: {
        profesionalId: number;
        status: RetentionStatus;
        pvpActual: number;
        variacionAbs: number | null;
        variacionPct: number | null;
      }
    ) => {
      const prev = map.get(alert.profesionalId);
      if (!prev || retentionStatusRank(alert.status) < retentionStatusRank(prev.status)) {
        map.set(alert.profesionalId, {
          status: alert.status,
          pvpActual: alert.pvpActual,
          variacionAbs: alert.variacionAbs,
          variacionPct: alert.variacionPct,
        });
      }
    };
    for (const row of retention?.professionalStatuses ?? []) {
      ingest(row);
    }
    for (const alert of retention?.topAlerts ?? []) {
      ingest(alert);
    }
    for (const branch of retention?.branches ?? []) {
      for (const alert of branch.topAlerts) {
        ingest(alert);
      }
    }
    return map;
  }, [retention]);

  const branchesByProfessional = useMemo(() => {
    const map = new Map<number, Array<{ id: number; name: string; turnos: number }>>();
    for (const branch of branchMainMetrics) {
      for (const pro of branch.professionals?.profesionales ?? []) {
        const list = map.get(pro.profesionalId) ?? [];
        list.push({
          id: branch.sede.id,
          name: branch.sede.nombre,
          turnos: pro.turnosAtendidos,
        });
        map.set(pro.profesionalId, list);
      }
    }
    for (const list of map.values()) {
      list.sort((a, b) => b.turnos - a.turnos);
    }
    return map;
  }, [branchMainMetrics]);

  const pvpById = useMemo(() => {
    const map = new Map<number, number>();
    for (const row of pvp?.profesionales ?? []) {
      map.set(row.profesionalId, row.value);
    }
    return map;
  }, [pvp]);

  const rows = useMemo(() => {
    const production = clinicProfessionals?.profesionales ?? [];
    return production.map((pro) => {
      const ret = retentionByProfessional.get(pro.profesionalId);
      const statusRaw = listedRetentionStatus(ret?.status);
      const uiStatus = toUiStatus(statusRaw);
      const pvpValue = ret?.pvpActual ?? pvpById.get(pro.profesionalId) ?? null;
      const variacionAbs = ret?.variacionAbs ?? null;
      const variacionPct = ret?.variacionPct ?? null;
      const branches = branchesByProfessional.get(pro.profesionalId) ?? [];
      const declining = pro.trend === 'down' || (variacionAbs !== null && variacionAbs < -0.3);
      const trend = trendSymbol(pro.trend, variacionAbs);

      return {
        id: pro.profesionalId,
        name: pro.nombre || `#${pro.profesionalId}`,
        turnos: pro.turnosAtendidos,
        pvp: pvpValue,
        variacionAbs,
        variacionPct,
        statusRaw,
        uiStatus,
        declining,
        trend,
        branches,
        branchIds: new Set(branches.map((b) => b.id)),
      };
    });
  }, [clinicProfessionals, retentionByProfessional, pvpById, branchesByProfessional]);

  const counts = useMemo(() => {
    let attention = 0;
    let declining = 0;
    let healthy = 0;
    for (const row of rows) {
      if (row.uiStatus === 'critical' || row.uiStatus === 'attention') attention += 1;
      if (row.declining) declining += 1;
      if (row.uiStatus === 'healthy' && !row.declining) healthy += 1;
    }
    return {
      active: rows.length,
      // Mismo criterio que el badge (Crítico + Atención). No usar el 0 de la API
      // cuando la tabla ya muestra gente en esos tonos.
      attention,
      declining,
      healthy,
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...rows]
      .filter((row) => {
        if (quickFilter === 'attention') {
          if (row.uiStatus !== 'critical' && row.uiStatus !== 'attention') return false;
        } else if (quickFilter === 'declining') {
          if (!row.declining) return false;
        } else if (quickFilter === 'healthy') {
          if (row.uiStatus !== 'healthy' || row.declining) return false;
        }
        if (branchFilter !== 'all' && !row.branchIds.has(branchFilter)) return false;
        if (q && !row.name.toLowerCase().includes(q)) return false;
        return true;
      })
      .sort(
        (a, b) =>
          uiStatusRank(a.uiStatus, a.declining) - uiStatusRank(b.uiStatus, b.declining) ||
          retentionStatusRank(a.statusRaw) - retentionStatusRank(b.statusRaw) ||
          (a.pvp ?? 999) - (b.pvp ?? 999) ||
          b.turnos - a.turnos
      );
  }, [rows, quickFilter, branchFilter, search]);

  const branchCompare = useMemo(() => {
    const retentionById = new Map((retention?.branches ?? []).map((b) => [b.consultorioId, b]));
    return branchMainMetrics
      .map((branch) => {
        const ret = retentionById.get(branch.sede.id);
        const pros = branch.professionals?.profesionales?.length ?? 0;
        return {
          id: branch.sede.id,
          name: branch.sede.nombre,
          professionals: pros,
          pvp: branch.pvp?.value ?? ret?.branchPvp ?? null,
          variance: ret?.branchPvpVariacionAbs ?? null,
          inAlert: ret?.professionalsInAttention ?? 0,
        };
      })
      .filter((row) => row.professionals > 0 || row.pvp !== null)
      .sort((a, b) => b.inAlert - a.inAlert || (a.pvp ?? 999) - (b.pvp ?? 999));
  }, [branchMainMetrics, retention]);

  const pvpAvg = retention?.clinicPvp ?? pvp?.value ?? null;
  const pvpDelta = retention?.clinicPvpVariacionAbs ?? null;

  const formatBranchLabel = (branches: Array<{ name: string }>) => {
    if (branches.length === 0) return '—';
    if (branches.length === 1) return branches[0].name;
    return t('clinicHub.professionalsTab.branchPlus', {
      name: branches[0].name,
      count: branches.length - 1,
    });
  };

  const formatVariance = (abs: number | null, pct: number | null) => {
    if (pct !== null) {
      return `${formatSigned(locale, pct)}%`;
    }
    if (abs !== null) {
      return `${formatSigned(locale, abs)} pts`;
    }
    return '—';
  };

  const openProfessional = (profesionalId: number) => {
    navigate(`/professionals/${profesionalId}?tenant=${tenantId}&period=${period}`);
  };

  const filterChips: Array<{ id: QuickFilter; label: string }> = [
    { id: 'all', label: t('clinicHub.professionalsTab.filters.all') },
    {
      id: 'attention',
      label: t('clinicHub.professionalsTab.filters.attention', { count: counts.attention }),
    },
    {
      id: 'declining',
      label: t('clinicHub.professionalsTab.filters.declining', { count: counts.declining }),
    },
    {
      id: 'healthy',
      label: t('clinicHub.professionalsTab.filters.healthy', { count: counts.healthy }),
    },
  ];

  return (
    <div className="flex min-h-0 flex-col gap-5 lg:h-full">
      {/* 1. Estado general — fijo */}
      <section className="shrink-0 space-y-2">
        <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
          {t('clinicHub.professionalsTab.kpisTitle')}
        </h2>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-4">
          <div>
            <p className="text-2xl font-bold tabular-nums text-gray-950 dark:text-white sm:text-3xl">
              {formatInteger(counts.active, locale)}
            </p>
            <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
              {t('clinicHub.professionalsTab.kpis.active')}
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold tabular-nums text-gray-950 dark:text-white sm:text-3xl">
              {formatInteger(counts.attention, locale)}
            </p>
            <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
              {t('clinicHub.professionalsTab.kpis.attention')}
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold tabular-nums text-gray-950 dark:text-white sm:text-3xl">
              {pvpAvg === null
                ? '—'
                : pvpAvg.toLocaleString(locale, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}
            </p>
            <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
              {t('clinicHub.professionalsTab.kpis.pvpAvg')}
            </p>
          </div>
          <div>
            <p
              className={`text-2xl font-bold tabular-nums sm:text-3xl ${
                pvpDelta === null
                  ? 'text-gray-950 dark:text-white'
                  : pvpDelta < -0.3
                    ? 'text-red-700 dark:text-red-300'
                    : pvpDelta > 0.3
                      ? 'text-teal-700 dark:text-teal-300'
                      : 'text-gray-950 dark:text-white'
              }`}
            >
              {pvpDelta === null
                ? '—'
                : `${pvpDelta < 0 ? '↓' : pvpDelta > 0 ? '↑' : ''} ${formatSigned(locale, Math.abs(pvpDelta))} pts`}
            </p>
            <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
              {t('clinicHub.professionalsTab.kpis.pvpDelta')}
            </p>
          </div>
        </div>
      </section>

      {/* 2. Rendimiento — colapsable; tabla con scroll propio */}
      <section className={`flex flex-col gap-2 ${performanceOpen ? 'min-h-0 lg:flex-1' : 'shrink-0'}`}>
        <button
          type="button"
          onClick={() => setPerformanceOpen((v) => !v)}
          className="flex shrink-0 w-full items-center justify-between gap-2 text-left"
          aria-expanded={performanceOpen}
        >
          <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
            <span className="mr-1.5 inline-block w-3 text-[10px]" aria-hidden>
              {performanceOpen ? '▼' : '▶'}
            </span>
            {t('clinicHub.professionalsTab.performanceTitle')}
          </h2>
          <span className="text-xs font-semibold text-teal-700 dark:text-teal-300">
            {performanceOpen
              ? t('clinicHub.professionalsTab.collapse')
              : t('clinicHub.professionalsTab.expand')}
          </span>
        </button>

        {performanceOpen ? (
          <>
            <div className="flex shrink-0 flex-wrap gap-2">
              {filterChips.map((chip) => {
                const active = quickFilter === chip.id;
                return (
                  <button
                    key={chip.id}
                    type="button"
                    onClick={() => setQuickFilter(chip.id)}
                    className={`rounded-md border px-2.5 py-1 text-xs font-semibold transition ${
                      active
                        ? 'border-teal-600 bg-teal-50 text-teal-800 dark:border-teal-500 dark:bg-teal-950/40 dark:text-teal-200'
                        : 'border-[var(--bi-line)] bg-white text-gray-700 hover:bg-slate-50 dark:bg-white/5 dark:text-gray-200 dark:hover:bg-white/10'
                    }`}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="clinic-pro-branch-filter">
                {t('clinicHub.professionalsTab.branchFilter')}
              </label>
              <select
                id="clinic-pro-branch-filter"
                value={branchFilter === 'all' ? 'all' : String(branchFilter)}
                onChange={(e) => {
                  const v = e.target.value;
                  setBranchFilter(v === 'all' ? 'all' : Number(v));
                }}
                className="rounded-md border border-[var(--bi-line)] bg-white px-2.5 py-1.5 text-sm text-gray-800 dark:bg-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('clinicHub.professionalsTab.allBranches')}</option>
                {branchMainMetrics.map((b) => (
                  <option key={b.sede.id} value={b.sede.id}>
                    {b.sede.nombre}
                  </option>
                ))}
              </select>

              <label className="sr-only" htmlFor="clinic-pro-search">
                {t('clinicHub.professionalsTab.search')}
              </label>
              <input
                id="clinic-pro-search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('clinicHub.professionalsTab.searchPlaceholder')}
                className="min-w-[12rem] flex-1 rounded-md border border-[var(--bi-line)] bg-white px-2.5 py-1.5 text-sm text-gray-800 dark:bg-gray-900 dark:text-gray-100"
              />
            </div>

            <div className="overflow-x-auto rounded-lg border border-[var(--bi-line)] bg-white/80 dark:bg-white/5 lg:min-h-0 lg:flex-1 lg:overflow-auto lg:overscroll-contain">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 z-10 border-b border-[var(--bi-line)] bg-slate-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900 dark:text-gray-400">
                  <tr>
                    <th className="px-3 py-2.5 font-semibold">{t('clinicHub.professionalsTab.cols.name')}</th>
                    <th className="px-3 py-2.5 font-semibold">{t('clinicHub.professionalsTab.cols.branch')}</th>
                    <th className="px-3 py-2.5 font-semibold tabular-nums">
                      {t('clinicHub.professionalsTab.cols.appointments')}
                    </th>
                    <th className="px-3 py-2.5 font-semibold tabular-nums">
                      {t('clinicHub.professionalsTab.cols.pvp')}
                    </th>
                    <th className="px-3 py-2.5 font-semibold tabular-nums">
                      {t('clinicHub.professionalsTab.cols.variance')}
                    </th>
                    <th className="px-3 py-2.5 font-semibold">{t('clinicHub.professionalsTab.cols.trend')}</th>
                    <th className="px-3 py-2.5 font-semibold">{t('clinicHub.professionalsTab.cols.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr
                      key={row.id}
                      className="cursor-pointer border-b border-[var(--bi-line)] last:border-0 hover:bg-slate-50/80 dark:hover:bg-white/5"
                      onClick={() => openProfessional(row.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openProfessional(row.id);
                        }
                      }}
                      tabIndex={0}
                      role="link"
                    >
                      <td className="px-3 py-2.5 font-semibold text-gray-900 dark:text-white">{row.name}</td>
                      <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300">
                        {formatBranchLabel(row.branches)}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-gray-800 dark:text-gray-100">
                        {formatInteger(row.turnos, locale)}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-gray-800 dark:text-gray-100">
                        {row.pvp === null
                          ? '—'
                          : row.pvp.toLocaleString(locale, { maximumFractionDigits: 1 })}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-gray-700 dark:text-gray-200">
                        {formatVariance(row.variacionAbs, row.variacionPct)}
                      </td>
                      <td className="px-3 py-2.5 text-gray-700 dark:text-gray-200">
                        {row.trend === 'up' ? '↑' : row.trend === 'down' ? '↓' : '→'}
                      </td>
                      <td className="px-3 py-2.5">
                        {row.statusRaw ? (
                          <OwnerStatusBadge
                            label={t(presentRetentionStatus(row.statusRaw).labelKey)}
                            tip={t(presentRetentionStatus(row.statusRaw).tipKey)}
                            tone={presentRetentionStatus(row.statusRaw).tone}
                            testId={`hub-pro-status-${row.statusRaw}`}
                          />
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredRows.length === 0 && (
                <p className="px-3 py-6 text-sm text-gray-500 dark:text-gray-400">
                  {t('clinicHub.professionalsTab.empty')}
                </p>
              )}
            </div>
          </>
        ) : null}
      </section>

      {/* 3. Comparativa por sucursal — colapsable */}
      {branchMainMetrics.length > 1 ? (
        <section
          className={`flex shrink-0 flex-col gap-2 ${branchCompareOpen ? 'max-h-[34vh]' : ''}`}
        >
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setBranchCompareOpen((v) => !v)}
              className="flex min-w-0 flex-1 items-center gap-1 text-left"
              aria-expanded={branchCompareOpen}
            >
              <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                <span className="mr-1.5 inline-block w-3 text-[10px]" aria-hidden>
                  {branchCompareOpen ? '▼' : '▶'}
                </span>
                {t('clinicHub.professionalsTab.branchCompareTitle')}
              </h2>
              <span className="ml-auto text-xs font-semibold text-teal-700 dark:text-teal-300">
                {branchCompareOpen
                  ? t('clinicHub.professionalsTab.collapse')
                  : t('clinicHub.professionalsTab.expand')}
              </span>
            </button>
            {branchFilter !== 'all' ? (
              <button
                type="button"
                onClick={() => setBranchFilter('all')}
                className="text-sm font-semibold text-teal-700 hover:underline dark:text-teal-300"
              >
                {t('clinicHub.professionalsTab.clearBranchFilter')}
              </button>
            ) : null}
          </div>
          {branchCompareOpen ? (
            isBranchMetricsLoading ? (
              <div className="max-w-2xl rounded-lg border border-[var(--bi-line)] bg-white/80 dark:bg-white/5">
                <LoadingBlock size="sm" minHeightClassName="min-h-[5rem]" />
              </div>
            ) : branchCompare.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('clinicHub.professionalsTab.branchCompareEmpty')}
              </p>
            ) : (
              <div className="min-h-0 max-w-2xl flex-1 overflow-auto overscroll-contain rounded-lg border border-[var(--bi-line)] bg-white/80 dark:bg-white/5">
                <table className="min-w-full text-left text-sm">
                  <thead className="sticky top-0 z-10 border-b border-[var(--bi-line)] bg-slate-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900 dark:text-gray-400">
                    <tr>
                      <th className="px-3 py-2.5 font-semibold">
                        {t('clinicHub.professionalsTab.branchCols.branch')}
                      </th>
                      <th className="px-3 py-2.5 font-semibold tabular-nums">
                        {t('clinicHub.professionalsTab.branchCols.professionals')}
                      </th>
                      <th className="px-3 py-2.5 font-semibold tabular-nums">
                        {t('clinicHub.professionalsTab.branchCols.pvp')}
                      </th>
                      <th className="px-3 py-2.5 font-semibold tabular-nums">
                        {t('clinicHub.professionalsTab.branchCols.variance')}
                      </th>
                      <th className="px-3 py-2.5 font-semibold tabular-nums">
                        {t('clinicHub.professionalsTab.branchCols.inAlert')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {branchCompare.map((branch) => (
                      <tr
                        key={branch.id}
                        className="cursor-pointer border-b border-[var(--bi-line)] last:border-0 hover:bg-slate-50/80 dark:hover:bg-white/5"
                        onClick={() => setBranchFilter(branch.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setBranchFilter(branch.id);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={t('clinicHub.professionalsTab.filterByBranchAria', {
                          name: branch.name,
                        })}
                      >
                        <td className="px-3 py-2.5 font-semibold text-gray-900 dark:text-white">
                          {branch.name}
                          {branchFilter === branch.id ? (
                            <span className="ml-2 text-xs font-medium text-teal-700 dark:text-teal-300">
                              {t('clinicHub.professionalsTab.filtering')}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums">
                          {formatInteger(branch.professionals, locale)}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums">
                          {branch.pvp === null
                            ? '—'
                            : branch.pvp.toLocaleString(locale, { maximumFractionDigits: 1 })}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums">
                          {branch.variance === null
                            ? '—'
                            : `${formatSigned(locale, branch.variance)} pts`}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums">
                          {formatInteger(branch.inAlert, locale)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
